import { AlphabetUtils } from './alphabet-utils.js';
import { SHORT_CODE_ALPHABET, SHORT_CODE_LENGTH } from '../models/serializable/serial-numbers/short-code-pool.js';
import { ShuffleUtils } from './shuffle-utils.js';
import { MersennePrimeUtils } from './mersenne-prime-utils.js';
import { Result } from './result.js';

export class ScannerEventCodeUtils {
  private static readonly EVENT_SHORT_CODE_LANGUAGE_CARDINALITY = AlphabetUtils.getCardinalityOfLanguage(SHORT_CODE_ALPHABET, SHORT_CODE_LENGTH);
  private static readonly PRIME = MersennePrimeUtils.findPrimeLargerThan(this.EVENT_SHORT_CODE_LANGUAGE_CARDINALITY).value!;
  private static readonly EVENT_SHORT_CODE_OFFSET = this.PRIME;

  private static readonly SCANNER_CODE_ALPHABET = AlphabetUtils.RESTRICTED_ALPHA_NUMERIC;
  private static readonly SCANNER_CODE_LENGTH = Math.ceil(SHORT_CODE_LENGTH * Math.log(SHORT_CODE_ALPHABET.length) / Math.log(this.SCANNER_CODE_ALPHABET.length));
  private static readonly SCANNER_CODE_LANGUAGE_CARDINALITY = AlphabetUtils.getCardinalityOfLanguage(this.SCANNER_CODE_ALPHABET, this.SCANNER_CODE_LENGTH);
  // NOTE(emil): setting this to non zero will not improve the letter distributions.
  private static readonly SCANNER_CODE_OFFSET = BigInt(0);

  public static findScannerEventCode(
      eventShortCode: string,
  ): Result<string, string> {
    if(eventShortCode.length !== SHORT_CODE_LENGTH) {
      return Result.error(`Event short code {${eventShortCode}}, must be {${SHORT_CODE_LENGTH}}, characters long.`);
    }

    const shuffledEventShortCodeResult = this.shuffleEventShortCode(eventShortCode);
    if(shuffledEventShortCodeResult.isError) {
      return Result.error(shuffledEventShortCodeResult.error);
    }

    const shuffledEventShortCode = shuffledEventShortCodeResult.value;

    const scannerCodeResult = AlphabetUtils.translate(
        shuffledEventShortCode,
        SHORT_CODE_ALPHABET,
        this.SCANNER_CODE_ALPHABET,
    );

    if(scannerCodeResult.isError) {
      return Result.error(scannerCodeResult.error);
    }

    const scannerCode = scannerCodeResult.value;
    return this.shuffleScannerCode(scannerCode);
  }

  private static shuffleEventShortCode(eventShortCode: string): Result<string, string> {
    const eventShortCodeIntResult = AlphabetUtils.wordToBigInt(eventShortCode, SHORT_CODE_ALPHABET);
    if(eventShortCodeIntResult.isError) {
      return Result.error(eventShortCodeIntResult.error);
    }

    const eventShortCodeInt = eventShortCodeIntResult.value;

    const shuffledEventShortCodeIntResult = ShuffleUtils.findShuffledInteger(
        eventShortCodeInt,
        this.EVENT_SHORT_CODE_LANGUAGE_CARDINALITY,
        this.EVENT_SHORT_CODE_OFFSET,
    );

    if(shuffledEventShortCodeIntResult.isError) {
      return Result.error(shuffledEventShortCodeIntResult.error);
    }

    const shuffledEventShortCodeInt = shuffledEventShortCodeIntResult.value;

    return AlphabetUtils.bigIntToWord(shuffledEventShortCodeInt, SHORT_CODE_ALPHABET, SHORT_CODE_LENGTH);
  }

  private static shuffleScannerCode(scannerEventCode: string): Result<string, string> {
    const scannerEventCodeIntResult = AlphabetUtils.wordToBigInt(scannerEventCode, this.SCANNER_CODE_ALPHABET);
    if(scannerEventCodeIntResult.isError) {
      return Result.error(scannerEventCodeIntResult.error);
    }

    const scannerEventCodeInt = scannerEventCodeIntResult.value;
    const shuffledScannerEventCodeIntResult = ShuffleUtils.findShuffledInteger(
        scannerEventCodeInt,
        this.SCANNER_CODE_LANGUAGE_CARDINALITY,
        this.SCANNER_CODE_OFFSET,
    );

    if(shuffledScannerEventCodeIntResult.isError) {
      return Result.error(shuffledScannerEventCodeIntResult.error);
    }

    const shuffledScannerEventCodeInt = shuffledScannerEventCodeIntResult.value;
    return AlphabetUtils.bigIntToWord(shuffledScannerEventCodeInt, this.SCANNER_CODE_ALPHABET, this.SCANNER_CODE_LENGTH);
  }

  public static findEventShortCode(
      scannerEventCode: string,
  ): Result<string, string> {
    if(scannerEventCode.length !== this.SCANNER_CODE_LENGTH) {
      return Result.error(`Scanner event code {${scannerEventCode}}, must be {${this.SCANNER_CODE_LENGTH}}, characters long.`);
    }

    const unshuffledScannerCodeResult = this.unshuffleScannerCode(scannerEventCode);
    if(unshuffledScannerCodeResult.isError) {
      return Result.error(unshuffledScannerCodeResult.error);
    }

    const unshuffledScannerCode = unshuffledScannerCodeResult.value;
    const shuffledEventShortCodeResult = AlphabetUtils.translate(
        unshuffledScannerCode,
        this.SCANNER_CODE_ALPHABET,
        SHORT_CODE_ALPHABET,
        SHORT_CODE_LENGTH,
    );

    if(shuffledEventShortCodeResult.isError) {
      return Result.error(shuffledEventShortCodeResult.error);
    }

    const shuffledEventShortCode = shuffledEventShortCodeResult.value;

    return this.unshuffleEventShortCode(shuffledEventShortCode);
  }

  private static unshuffleScannerCode(shuffledScannerCode: string): Result<string, string> {
    const shuffledScannerCodeIntResult = AlphabetUtils.wordToBigInt(shuffledScannerCode, this.SCANNER_CODE_ALPHABET);
    if(shuffledScannerCodeIntResult.isError) {
      return Result.error(shuffledScannerCodeIntResult.error);
    }

    const shuffledScannerCodeInt = shuffledScannerCodeIntResult.value;
    const scannerCodeIntResult = ShuffleUtils.findUnshuffledInteger(
        shuffledScannerCodeInt,
        this.SCANNER_CODE_LANGUAGE_CARDINALITY,
        this.SCANNER_CODE_OFFSET,
    );

    if(scannerCodeIntResult.isError) {
      return Result.error(scannerCodeIntResult.error);
    }

    const scannerCodeInt = scannerCodeIntResult.value;
    return AlphabetUtils.bigIntToWord(scannerCodeInt, this.SCANNER_CODE_ALPHABET, this.SCANNER_CODE_LENGTH);
  }

  private static unshuffleEventShortCode(shuffledShortCode: string): Result<string, string> {
    const shuffledShortCodeIntResult = AlphabetUtils.wordToBigInt(shuffledShortCode, SHORT_CODE_ALPHABET);
    if(shuffledShortCodeIntResult.isError) {
      return Result.error(shuffledShortCodeIntResult.error);
    }

    const shuffledShortCodeInt = shuffledShortCodeIntResult.value;

    const shortCodeIntResult = ShuffleUtils.findUnshuffledInteger(
        shuffledShortCodeInt,
        this.EVENT_SHORT_CODE_LANGUAGE_CARDINALITY,
        this.EVENT_SHORT_CODE_OFFSET,
    );

    if(shortCodeIntResult.isError) {
      return Result.error(shortCodeIntResult.error);
    }

    const shortCodeInt = shortCodeIntResult.value;

    return AlphabetUtils.bigIntToWord(shortCodeInt, SHORT_CODE_ALPHABET, SHORT_CODE_LENGTH);
  }
}
